import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { EmptyPlaceholder } from '@/components/EmptyPlaceholder';
import { useAuth } from '@/context/auth';
import SmsService from '@/api/sms';
import CampaignList from './CampaignList';
import CampaignForm from '@/components/sms/campaign/CampaignForm';
import { hasSuperAdminAccess, hasOrgAdminAccess, hasRestAdminAccess } from '@/context/auth/types';

interface CampaignsTabProps {
  onError?: (error: Error) => void;
}

type ViewMode = 'list' | 'create' | 'edit';

const CampaignsTab: React.FC<CampaignsTabProps> = ({ onError }) => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    if (!user?.restaurants || user.restaurants.length === 0) {
      const errorMessage = 'No restaurant access available';
      setError(errorMessage);
      setLoading(false);
      if (onError) onError(new Error(errorMessage));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get the first restaurant ID
      const restaurantId = user.restaurants[0].id;
      const organisationId = user.organisationId;
      
      // Fetch campaigns - for org admins, include org-wide campaigns
      const fetchedCampaigns = await SmsService.fetchCampaigns(
        restaurantId, 
        hasOrgAdminAccess(user.role) || hasSuperAdminAccess(user.role) ? organisationId : undefined
      );
      
      setCampaigns(fetchedCampaigns);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      const errorMessage = 'Failed to load campaigns. Please try again.';
      setError(errorMessage);
      if (onError && err instanceof Error) onError(err);
      
      toast({
        title: 'Error loading campaigns',
        description: 'There was a problem fetching your campaigns.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [user]);

  // Check if user can create campaigns
  const canCreateCampaign = user && (
    hasSuperAdminAccess(user.role) ||
    hasOrgAdminAccess(user.role) ||
    hasRestAdminAccess(user.role)
  );

  // View transition handlers
  const handleCreateCampaign = () => {
    setCurrentView('create');
    setSelectedCampaignId(null);
  };

  const handleEditCampaign = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setCurrentView('edit');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedCampaignId(null);
    fetchCampaigns(); // Refresh list
  };

  const handleCampaignSaved = () => {
    setCurrentView('list');
    fetchCampaigns(); // Refresh list with new campaign
  };

  // Campaign ideas
  const campaignIdeas = [
    {
      title: 'Welcome New Customers',
      description: 'Send a welcome message to customers after their first visit.',
      scenario: 'Segment: First-time visitors in the last 14 days'
    },
    {
      title: 'Win Back Lapsed Customers',
      description: 'Offer an incentive to customers who haven\'t visited recently.',
      scenario: 'Segment: Customers who haven\'t visited in 60+ days'
    },
    {
      title: 'VIP Customer Rewards',
      description: 'Thank your best customers with exclusive offers.',
      scenario: 'Segment: Top 20% of customers by spend'
    }
  ];

  // Render form views (create/edit)
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="py-4 space-y-6 overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-medium">
              {currentView === 'create' ? 'Create Campaign' : 'Edit Campaign'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {currentView === 'create'
                ? 'Create a new SMS campaign to engage your customers'
                : 'Modify campaign settings and schedule'
              }
            </p>
          </div>
          <Button variant="outline" onClick={handleBackToList}>
            Back to Campaigns
          </Button>
        </div>

        <Separator />

        <CampaignForm
          campaignId={selectedCampaignId}
          onSuccess={handleCampaignSaved}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Empty state - no campaigns
  if (!loading && !error && campaigns.length === 0) {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Campaigns</h3>
          {canCreateCampaign && (
            <Button onClick={handleCreateCampaign} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Campaign
            </Button>
          )}
        </div>

        <EmptyPlaceholder
          title="No campaigns yet"
          description="Create your first SMS campaign to start engaging with your customers."
          icon={<AlertCircle className="h-12 w-12" />}
        >
          {canCreateCampaign && (
            <Button onClick={handleCreateCampaign}>
              New Campaign
            </Button>
          )}
        </EmptyPlaceholder>

        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">Campaign Ideas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {campaignIdeas.map((idea, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-base">{idea.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{idea.description}</CardDescription>
                </CardContent>
                <CardFooter>
                  <p className="text-xs text-muted-foreground">{idea.scenario}</p>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Campaigns</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchCampaigns}
            disabled={loading}
            size="icon"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>

          {canCreateCampaign && (
            <Button onClick={handleCreateCampaign} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Campaign
            </Button>
          )}
        </div>
      </div>

      <Separator />

      <CampaignList
        campaigns={campaigns}
        loading={loading}
        error={error}
        onRefresh={fetchCampaigns}
      />
    </div>
  );
};

export default CampaignsTab;
